import React from "react";

type AuthFieldType = "email" | "password" | "text";

interface AuthFormFieldProps {
  id: string;
  label: string;
  type: AuthFieldType;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  placeholder?: string;
  error?: string;
  required?: boolean;
}

/**
 * One form field (label + input + inline error) for the auth pages.
 *
 * The original login/register screens duplicated ~20 lines per field; this
 * consolidates the label layout, input classes, error styling, and required
 * asterisk so every auth field looks identical. Non-auth pages intentionally
 * don't use this — they have their own richer field primitives (see Deposit).
 */
const AuthFormField: React.FC<AuthFormFieldProps> = ({
  id,
  label,
  type,
  value,
  onChange,
  autoComplete,
  placeholder,
  error,
  required,
}) => {
  const borderClass = error ? "border-red-500" : "border-gray-300";

  return (
    <div>
      <label
        htmlFor={id}
        className="block mb-2 text-sm font-medium text-gray-900"
      >
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      <input
        id={id}
        name={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        placeholder={placeholder}
        className={`bg-white border text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${borderClass}`}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default AuthFormField;
